import { Button, Card, ConfigProvider, Flex, Select, Table } from "antd";
import cx from "classnames";
import styles from "./InputFix.module.css";
import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";

// ---- Icons ----
const IconFirst = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <path d="M9.5 15L4.5 10L9.5 5M15.5 15L10.5 10L15.5 5" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconPrev = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconNext = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconLast = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <path d="M10.5 15L15.5 10L10.5 5M4.5 15L9.5 10L4.5 5" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ---- Custom pagination bar ----
function CustomPagination({
  current = 1,
  pageSize = 10,
  total = 0,
  pageSizeOptions = [10, 25, 50, 100],
  onChange = () => { },
}) {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const goTo = (page) => {
    const clamped = Math.min(Math.max(page, 1), totalPages);
    if (clamped !== current) onChange(clamped, pageSize);
  };

  const iconBtnStyle = (disabled) => ({
    width: 32,
    height: 32,
    minWidth: 32,
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid #D0D5DD",
    color: disabled ? "#D0D5DD" : "#667085",
  });

  return (
    <Flex align="center" justify="center" gap={20} wrap="wrap" >
      <Flex align="center" gap={8}>
        <span style={{ color: "#667085" }}>{t("rowsPerPage") || "Rows per page:"}</span>
        <Select
          size="middle"
          value={pageSize}
          style={{ width: 72 }}
          onChange={(size) => onChange(1, size)}
          options={pageSizeOptions.map((n) => ({ value: n, label: n }))}
        />
      </Flex>

      <Flex align="center" gap={8}>
        <span style={{ color: "#667085" }}>{t("page") || "Page"}</span>
        <Select
          size="middle"
          value={current}
          style={{ width: 72 }}
          onChange={(p) => goTo(p)}
          options={Array.from({ length: totalPages }, (_, i) => ({
            value: i + 1,
            label: i + 1,
          }))}
        />
        <span style={{ color: "#667085" }}>
          {t("of") || "of"} {totalPages}, {t("total") || "total"}: {total}
        </span>
      </Flex>

      <Flex align="center" gap={6}>
        <Button shape="circle" disabled={current === 1} style={iconBtnStyle(current === 1)} onClick={() => goTo(1)}>
          <IconFirst />
        </Button>
        <Button shape="circle" disabled={current === 1} style={iconBtnStyle(current === 1)} onClick={() => goTo(current - 1)}>
          <IconPrev />
        </Button>
        <Button
          shape="circle"
          style={{ width: 32, height: 32, minWidth: 32, padding: 0, border: "1px solid #D0D5DD", color: "#344054" }}
        >
          {current}
        </Button>
        <Button
          shape="circle"
          disabled={current === totalPages}
          style={iconBtnStyle(current === totalPages)}
          onClick={() => goTo(current + 1)}
        >
          <IconNext />
        </Button>
        <Button
          shape="circle"
          disabled={current === totalPages}
          style={iconBtnStyle(current === totalPages)}
          onClick={() => goTo(totalPages)}
        >
          <IconLast />
        </Button>
      </Flex>
    </Flex>
  );
}

// ---- Table wrapper ----
function TableAntdFix({
  loading = false,
  pagination = {},
  dataSource = [],
  columns = [],
  onChange = () => { },
  expandable = {},
  scroll = {},
  rowKey = null,
  noPage = false,
  itemRender = null,
  rowSelection = false,
  tableLayout = "",
  sticky = {},
  onRowClick = () => { },
  className = "",
  components = {},
}) {
  const tableRef = useRef(null);

  useEffect(() => {
    if (pagination.current && tableRef.current) {
      const body = tableRef.current.getElementsByClassName("ant-table-body")[0];
      if (body) body.scrollTop = 0;
    }
  }, [pagination]);

  console.log("pagination", pagination);

  // Called by CustomPagination
  const handlePaginationChange = (page, pageSize) => {
    console.log("pppp", page,
      pageSize);

    onChange(
      { pageNo: page, page: page, current: page, pageSize },
      {},
      {},
      { action: "paginate" }
    );

    if (tableRef.current) {
      setTimeout(() => {
        window.scrollTo({ top: tableRef.current.offsetTop - 20, behavior: "smooth" });
      }, 100);
    }
  };

  return (
    <Card styles={{ body: { padding: 0 } }}>
      <ConfigProvider
        theme={{
          components: {
            Table: { headerBg: "#F5F6F7" },
          },
        }}
      >
        <div ref={tableRef}>
          <Table
            className={cx(className, styles.table)}
            onRow={(record, rowIndex) => ({ onClick: onRowClick })}
            loading={loading}
            pagination={noPage ? false : {
              className: styles.pagination,
              position: ['bottomCenter'],
              current: pagination.current || 1,
              pageSize: pagination.pageSize || 10,   // ← typo fixed: was pagination.size
              total: pagination.total || 0,
              itemRender: (page, type, originalElement) => {
                if (type !== 'prev') return null;   // suppress every item except one anchor slot
                return (
                  <CustomPagination
                    current={pagination.page || 1}
                    pageSize={pagination.pageSize || 10}
                    total={pagination.total || 0}
                    pageSizeOptions={pagination.pageSizeOptions}
                    onChange={handlePaginationChange}
                  />
                );
              },
              showTotal: false,
              showLessItems: true,
              showSizeChanger: false,
              ...pagination,
            }}
            rowKey={rowKey}
            expandable={expandable}
            dataSource={dataSource}
            sticky={sticky}
            columns={columns}
            scroll={{ ...scroll }}
            rowSelection={rowSelection}
            tableLayout={tableLayout}
            style={{ border: "1px solid #EFF1F4", borderRadius: "8px" }}
            components={components}
          />
        </div>
      </ConfigProvider>
    </Card>
  );
}

export default TableAntdFix;